import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate } from '~/types/dates'
import type { SingletonKey } from '~/types/ids'
import { SINGLETON_KEY } from '~/types/ids'
import type { Centimetres } from '~/types/units'
import { toCentimetres, unwrapNumber } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * Athlete profile — a singleton (D-013).
 *
 * Checkpoint 1.1 persists the D-037 onboarding minimum. The remaining profile
 * fields from docs/data-model.md (training experience, physique goal, decision
 * preferences, constraints) are optional and arrive with the onboarding UI.
 *
 * Age is NOT stored. Date of birth is stored and age derived (D-038).
 */
export interface AthleteProfileRecord extends RecordEnvelope {
  readonly id: SingletonKey
  readonly heightCm: Centimetres
  readonly dateOfBirth: CalendarDate
  readonly trainingExperience?: string
  readonly primaryGoal?: string
  readonly preferredStyle?: string
  readonly constraints?: string
  /** Default from docs/data-model.md: avoid multiple changes in one week. */
  readonly avoidMultipleWeeklyChanges: boolean
}

export interface AthleteProfileInput {
  readonly heightCm: number
  readonly dateOfBirth: string
  readonly trainingExperience?: string
  readonly primaryGoal?: string
  readonly preferredStyle?: string
  readonly constraints?: string
  readonly avoidMultipleWeeklyChanges?: boolean
}

/** Plausible adult standing height, used only to catch entry errors. */
const MIN_HEIGHT_CM = 100
const MAX_HEIGHT_CM = 260

export function validateAthleteProfile(input: AthleteProfileInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const height = toCentimetres(input.heightCm)
  if (height === undefined) {
    issues.push(issue('heightCm', 'Height must be a positive number in centimetres.'))
  } else {
    const value = unwrapNumber(height)
    if (value < MIN_HEIGHT_CM || value > MAX_HEIGHT_CM) {
      issues.push(
        issue('heightCm', `Height must be between ${MIN_HEIGHT_CM} and ${MAX_HEIGHT_CM} cm.`),
      )
    }
  }

  if (toCalendarDate(input.dateOfBirth) === undefined) {
    issues.push(issue('dateOfBirth', 'Date of birth must be a real date in YYYY-MM-DD form.'))
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

export const ATHLETE_PROFILE_KEY: SingletonKey = SINGLETON_KEY

/**
 * Build a validated record without touching storage.
 *
 * Separating construction from persistence is what lets onboarding validate and
 * build profile, phase, and goal first, then commit all three in one atomic
 * transaction.
 */
export function buildAthleteProfileRecord(
  input: AthleteProfileInput,
  now: IsoTimestamp,
  existingCreatedAt?: IsoTimestamp,
): Result<AthleteProfileRecord, ValidationIssues> {
  const issues = validateAthleteProfile(input)
  if (issues.length > 0) return err(issues)

  const heightCm = toCentimetres(input.heightCm)
  const dateOfBirth = toCalendarDate(input.dateOfBirth)
  if (heightCm === undefined || dateOfBirth === undefined) {
    return err([issue('profile', 'Profile failed unit or date construction.')])
  }

  return ok({
    id: ATHLETE_PROFILE_KEY,
    heightCm,
    dateOfBirth,
    avoidMultipleWeeklyChanges: input.avoidMultipleWeeklyChanges ?? true,
    ...(input.trainingExperience === undefined
      ? {}
      : { trainingExperience: input.trainingExperience }),
    ...(input.primaryGoal === undefined ? {} : { primaryGoal: input.primaryGoal }),
    ...(input.preferredStyle === undefined ? {} : { preferredStyle: input.preferredStyle }),
    ...(input.constraints === undefined ? {} : { constraints: input.constraints }),
    createdAt: existingCreatedAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}
